import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import api from '../../services/api'
import { masterclassService } from '../../services/masterclassService'
import SpeakerImage from '../masterclass/SpeakerImage'
import Reveal from './Reveal'

const WORK = [
  {
    title: 'Kampala City Scape',
    category: 'Photography',
    image: '/Gallery/Academy/showcase/Kampala City Scape.jpg',
  },
  {
    title: 'Design thinking',
    category: 'Graphic design',
    image: '/Gallery/Academy/showcase/thinking.jpg',
  },
  {
    title: 'Video editing workflow',
    category: 'Video Production',
    image: '/Gallery/Academy/Workshops/ww.jpg',
  },
]

const HERO_FRAMES = [
  {
    src: '/Gallery/Academy/Creative Training Sessions/268A5754.JPG',
    alt: 'Students setting up a camera during SMA training',
    position: 'object-[center_30%]',
  },
  {
    src: '/Gallery/Academy/Workshops/ww.jpg',
    alt: 'A student editing during a production',
    position: 'object-center',
  },
  {
    src: '/Gallery/Academy/Creative Training Sessions/268A5756.JPG',
    alt: 'An SMA crew on a production shoot',
    position: 'object-[center_40%]',
  },
  {
    src: '/Gallery/Academy/showcase/Kampala City Scape.jpg',
    alt: 'Kampala City Scape, photography from SMA',
    position: 'object-[center_40%]',
  },
]

const SYNC_MARKS = ['Local media', 'Local analysis', 'Sync', 'Review']

function HeroSequence() {
  const [index, setIndex] = useState(0)

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    if (media.matches) return undefined
    const id = window.setInterval(() => {
      if (document.hidden) return
      setIndex((current) => (current + 1) % HERO_FRAMES.length)
    }, 7000)
    return () => window.clearInterval(id)
  }, [])

  return (
    <div
      className="relative w-full aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5] overflow-hidden"
      role="img"
      aria-label="Students learning, creating, and producing work at SMA"
    >
      {HERO_FRAMES.map((frame, frameIndex) => (
        <img
          key={frame.src}
          src={frame.src}
          alt=""
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[1400ms] ease-out ${frame.position} ${frameIndex === index ? 'opacity-100' : 'opacity-0'}`}
          fetchPriority={frameIndex === 0 ? 'high' : 'low'}
        />
      ))}
    </div>
  )
}

function formatSessionDate(value) {
  if (!value || Number.isNaN(new Date(value).getTime())) return null
  return new Date(value).toLocaleDateString('en-UG', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'Africa/Kampala',
  })
}

function isCurrentSession(session) {
  if (!session?.topic || !session?.event_date) return false
  const event = new Date(session.event_date)
  if (Number.isNaN(event.getTime())) return false
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Kampala' })
  const when = event.toLocaleDateString('en-CA', { timeZone: 'Africa/Kampala' })
  return when >= today
}

function formatSessionTime(value) {
  if (!value) return null
  const match = String(value).match(/^(\d{1,2}):(\d{2})/)
  if (!match) return null
  const hour = Number(match[1])
  if (Number.isNaN(hour)) return null
  const suffix = hour >= 12 ? 'PM' : 'AM'
  const hour12 = hour % 12 || 12
  return `${hour12}:${match[2]} ${suffix}`
}

export default function PlatformHome() {
  const [featured, setFeatured] = useState(null)
  const [session, setSession] = useState(null)

  useEffect(() => {
    let cancelled = false
    api.get('/creative-tools')
      .then(({ data }) => { if (!cancelled) setFeatured(data?.featured || null) })
      .catch(() => {})
    masterclassService.getUpcoming()
      .then((data) => { if (!cancelled) setSession(data?.masterclass || null) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  const syncName = featured?.name || 'SMA Sync'
  const syncLine = featured?.headline || 'Synchronization without the assumptions.'
  const syncStatus = featured?.status_label || 'In development'
  const syncPath = featured?.explore_path || null
  const sessionDate = formatSessionDate(session?.event_date)
  const sessionTime = formatSessionTime(session?.start_time)
  const hasSession = isCurrentSession(session)

  return (
    <>
      <header className="relative border-b border-white/10">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 pt-28 pb-12 sm:pt-32 sm:pb-16 lg:pt-36">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-end">
            <Reveal className="lg:col-span-7">
              <p className="text-[0.7rem] uppercase tracking-[0.28em] text-cinema-gold font-sans font-semibold">
                Synergy Multimedia Academy
              </p>
              <h1 className="mt-5 max-w-3xl font-display text-4xl sm:text-6xl lg:text-[4.5rem] text-white font-light tracking-tight leading-[1.02]">
                Learn creative skills. Create real work.
              </h1>
              <p className="mt-5 max-w-md text-sm sm:text-base text-cinema-soft/85 leading-relaxed font-sans">
                Practical creative education, professional tools and real-world opportunities for people building in the creative industry.
              </p>
              <Link to="/programs" className="cinema-btn-primary mt-8">
                Explore Programs
              </Link>
            </Reveal>
            <Reveal delay={80} className="lg:col-span-5">
              <HeroSequence />
            </Reveal>
          </div>
        </div>
      </header>

      <section className="border-b border-white/10" aria-labelledby="home-tools">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-14 sm:py-20">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] text-cinema-gold font-sans font-semibold">Creative Tools</p>
            <h2 id="home-tools" className="mt-3 max-w-3xl font-display text-3xl sm:text-5xl text-white font-light tracking-tight">
              The tools behind the work.
            </h2>
            <p className="mt-3 max-w-xl text-sm text-cinema-muted font-sans leading-relaxed">
              SMA is building professional creative software for the people who make things.
            </p>
          </Reveal>

          <div className="mt-10 border-t border-white/10 pt-8 grid lg:grid-cols-12 gap-8 lg:gap-12 items-stretch">
            <div className="lg:col-span-7 flex flex-col">
              <div className="flex items-baseline justify-between gap-4">
                <p className="text-[0.65rem] uppercase tracking-[0.2em] text-cinema-gold font-sans">{syncStatus}</p>
                <p className="text-[0.65rem] uppercase tracking-[0.2em] text-cinema-muted font-sans">Video</p>
              </div>
              <h3 className="mt-4 font-display text-4xl sm:text-6xl text-white font-light tracking-tight">{syncName}</h3>
              <p className="mt-3 max-w-lg font-display text-2xl sm:text-3xl text-cinema-soft font-light leading-snug">
                {syncLine}
              </p>
              <ul className="mt-8 border-t border-white/10">
                {['Offline-first', 'GPU accelerated', 'Built for real production'].map((item) => (
                  <li key={item} className="py-3 border-b border-white/10 text-sm text-white font-sans">
                    {item}
                  </li>
                ))}
              </ul>
              <p className="mt-6 max-w-lg text-sm text-cinema-muted font-sans leading-relaxed">
                An offline-first synchronization tool for real-world multi-camera production. Not available to download yet.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3">
                <Link to="/creative-tools" className="cinema-btn-text text-cinema-gold">
                  Explore Creative Tools
                  <span aria-hidden="true">→</span>
                </Link>
                {syncPath && (
                  <Link to={syncPath} className="text-sm text-cinema-muted hover:text-white font-sans transition-colors">
                    {syncName}
                  </Link>
                )}
              </div>
            </div>

            {/* Replace this panel with a product still or short film when SMA Sync has one. */}
            <figure className="lg:col-span-5 min-h-[280px] border border-white/10 flex flex-col justify-between p-6 sm:p-8">
              <figcaption className="text-[0.65rem] uppercase tracking-[0.2em] text-cinema-muted font-sans">
                On the machine
              </figcaption>
              <ol>
                {SYNC_MARKS.map((mark, index) => (
                  <li key={mark} className="flex items-baseline gap-4 py-2">
                    <span className="text-[0.65rem] font-mono text-cinema-gold tracking-[0.16em]">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <span className="font-display text-2xl sm:text-3xl text-white font-light">{mark}</span>
                  </li>
                ))}
              </ol>
              <p className="text-xs text-cinema-muted font-sans">Footage stays local.</p>
            </figure>
          </div>
        </div>
      </section>

      <section id="story" className="border-b border-white/10" aria-labelledby="home-academy">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-14 sm:py-20 grid lg:grid-cols-12 gap-8 lg:gap-14 items-center">
          <Reveal className="lg:col-span-5 order-2 lg:order-1">
            <p className="text-xs uppercase tracking-[0.22em] text-cinema-gold font-sans font-semibold">Academy</p>
            <h2 id="home-academy" className="mt-3 font-display text-4xl sm:text-5xl text-white font-light tracking-tight">
              Learn by doing.
            </h2>
            <p className="mt-4 text-sm text-cinema-muted font-sans leading-relaxed max-w-md">
              Practical creative programs built around real work.
            </p>
            <Link to="/programs" className="cinema-btn-text mt-8 text-cinema-gold">
              Explore Programs
              <span aria-hidden="true">→</span>
            </Link>
          </Reveal>
          <Reveal delay={80} className="lg:col-span-7 order-1 lg:order-2">
            <img
              src="/Gallery/Academy/Creative Training Sessions/DSC02423.jpg"
              alt="Hands-on creative training at SMA"
              loading="lazy"
              className="w-full aspect-[16/10] object-cover"
            />
          </Reveal>
        </div>
      </section>

      <section className="border-b border-white/10" aria-labelledby="home-made">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-14 sm:py-20">
          <Reveal className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-cinema-gold font-sans font-semibold">Made at SMA</p>
              <h2 id="home-made" className="mt-3 max-w-xl font-display text-3xl sm:text-5xl text-white font-light tracking-tight">
                See what our creatives are making.
              </h2>
            </div>
            <Link to="/showcase" className="cinema-btn-text text-cinema-gold shrink-0">
              View Showcase
              <span aria-hidden="true">→</span>
            </Link>
          </Reveal>
          <div className="mt-10 grid md:grid-cols-3 gap-x-6 gap-y-8">
            {WORK.map((piece, index) => (
              <figure key={piece.title} className={index === 1 ? 'md:mt-10' : ''}>
                <img src={piece.image} alt={piece.title} loading="lazy" className="w-full aspect-[4/5] object-cover" />
                <figcaption className="pt-3">
                  <p className="text-[0.65rem] uppercase tracking-[0.18em] text-cinema-gold font-sans">{piece.category}</p>
                  <p className="mt-1 font-display text-xl text-white font-light">{piece.title}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-white/10" aria-labelledby="home-lab">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-14 sm:py-20 grid lg:grid-cols-12 gap-8 lg:gap-14 items-center">
          <Reveal className="lg:col-span-6">
            <p className="text-xs uppercase tracking-[0.22em] text-cinema-gold font-sans font-semibold">Project Lab</p>
            <h2 id="home-lab" className="mt-3 font-display text-4xl sm:text-6xl text-white font-light tracking-tight leading-[1.05]">
              Take your skills into real work.
            </h2>
            <p className="mt-5 max-w-md text-sm sm:text-base text-cinema-soft/80 font-sans leading-relaxed">
              Work on real creative projects, gain production experience and build work you can show.
            </p>
            <Link to="/project-lab" className="cinema-btn-text mt-8 text-cinema-gold">
              Explore Project Lab
              <span aria-hidden="true">→</span>
            </Link>
          </Reveal>
          <Reveal delay={80} className="lg:col-span-6">
            <img
              src="/Gallery/Academy/Creative Training Sessions/268A5756.JPG"
              alt="An SMA crew on a production shoot"
              loading="lazy"
              className="w-full aspect-[4/3] object-cover object-[center_40%]"
            />
          </Reveal>
        </div>
      </section>

      <section aria-labelledby="home-masterclass">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-14 sm:py-20 grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-4">
            <p className="text-xs uppercase tracking-[0.22em] text-cinema-gold font-sans font-semibold">Friday Masterclass</p>
            {session?.speaker_photo && (
              <div className="mt-6 max-w-[220px]">
                <SpeakerImage
                  speaker={{ photo_url: session.speaker_photo, name: session.speaker_name }}
                  alt={session.speaker_name || 'Masterclass speaker'}
                  fit="card"
                />
              </div>
            )}
          </div>
          <div className="lg:col-span-8 border-t lg:border-t-0 border-white/10 lg:border-l lg:pl-12 pt-8 lg:pt-0">
            <h2 id="home-masterclass" className="font-display text-3xl sm:text-5xl text-white font-light tracking-tight leading-[1.08]">
              {hasSession ? session.topic : 'Learn from people doing the work.'}
            </h2>
            {hasSession && session.speaker_name && (
              <p className="mt-4 font-display text-xl text-cinema-soft font-light">{session.speaker_name}</p>
            )}
            {hasSession && (sessionDate || sessionTime) && (
              <p className="mt-2 text-sm text-white font-sans">
                {[sessionDate, sessionTime].filter(Boolean).join(' · ')}
              </p>
            )}
            <p className="mt-4 max-w-xl text-sm text-cinema-muted font-sans leading-relaxed">
              {hasSession
                ? 'Learn from people doing the work.'
                : 'A practical session with working creatives. Come to learn the work, not to watch a lecture.'}
            </p>
            <Link to="/masterclass" className="cinema-btn-text mt-8 text-cinema-gold">
              Explore Masterclass
              <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  )
}
